import { forwardRef, useEffect, useRef, useState } from "react";
import cx from "../../../lib/cx.js";
import { ReactionIcon } from "../ReactionIcon/ReactionIcon.jsx";
import * as styles from "./ReactionButton.css.ts";

/**
 * The ReactionButton displays an icon and a count of how often the reaction was
 * already given. The button can be displayed as `active`, if e.g. the current
 * user already gave this reaction. The component supports ref forwarding.
 */
const ReactionButton = forwardRef(
  ({ reaction, count, className, active, onClick, disabled, ...rest }, ref) => {
    const [pending, setPending] = useState(false);
    const unmountedRef = useRef(false);

    useEffect(() => {
      return () => {
        unmountedRef.current = true;
      };
    }, []);

    const handleClick = async (e) => {
      if (!onClick) return;
      const retVal = onClick(e);
      if (retVal && typeof retVal.then === "function") {
        setPending(true);
        try {
          await retVal;
        } catch (error) {
          console.error(error);
        } finally {
          setPending(false);
        }
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        disabled={pending || disabled}
        className={cx(styles.base, active && styles.active, className)}
        onClick={handleClick}
        {...rest}
      >
        <div className={styles.content}>
          <ReactionIcon reaction={reaction} />
          {count > 0 && <div>{count}</div>}
        </div>
      </button>
    );
  },
);

ReactionButton.displayName = "ReactionButton";

export default ReactionButton;
